import { isPast, parseISO } from "date-fns";
import type React from "react";
import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type { EventDetailsDto } from "@/api/types";
import { AudiencePreview } from "@/components/AudienceSelector/AudiencePreview";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DateRange } from "@/components/DateRange/DateRange";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { EntityDetailsCard } from "@/components/EntityDetailsCard/EntityDetailsCard";
import { EventBadges } from "@/components/EventBadges/EventBadges";
import { EventSignUpWithGuestsModal } from "@/components/EventSignUpWithGuestsModal/EventSignUpWithGuestsModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { LinkExternal01Icon } from "@/components/Icons/LinkExternal01Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { UserFullNameLink } from "@/components/UserFullNameLink/UserFullNameLink";
import { Inline } from "@/design-system/Inline";
import { ToggleButton } from "@/design-system/ToggleButton";
import { canParticipateEvent, computeMaxGuests, getIsEventSharable } from "@/helpers/events";
import { isContentAutoTranslated } from "@/helpers/languages";
import { canManageEvents } from "@/helpers/permissions";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { eventMutations } from "@/queries/events/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EventDetailsConfirmExternalEventSignUpModal } from "./EventDetailsConfirmExternalEventSignUpModal";

interface EventDetailsProps {
  event: EventDetailsDto;
}

export function EventDetails({ event }: EventDetailsProps): React.ReactNode {
  const [isConfirmExternalSignUpModalOpened, confirmExternalSignUpModalHandler] = useBool(false);
  const [isSignUpWithGuestsModalOpen, signUpWithGuestsModalHandler] = useBool(false);

  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { componentProps, openDeleteModal } = useDeleteModal<{
    id: string;
  }>("event-delete-modal");
  const navigate = useNavigate();
  const hasPermission = usePermission();

  const signUpMutation = eventMutations.useEventSignUp();
  const signOffMutation = eventMutations.useEventSignOff();
  const deleteEventMutation = eventMutations.useDeleteEvent();
  const resolvedImage = event.image ?? sessionUser.project.backgroundImage;
  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
        disabled: t("common.label.ended"),
      },
      "data-testid": "context-menu-edit-btn",
      value: "edit",
      icon: <Edit05Icon />,
      type: "button",
      isHidden: !event.canEdit,
      callbackFn: () => {
        if (isPast(parseISO(event.endsAt))) {
          showFlashToast({
            variant: "neutral",
            message: t("common.label.ended"),
          });

          return;
        }
        void navigate(
          routes.events.edit({
            slug,
            id: event.id,
          }),
        );
      },
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      value: "duplicate",
      icon: <Copy01Icon />,
      type: "button",
      callbackFn: () =>
        void navigate(
          routes.events.create({
            slug,
          }),
          {
            state: {
              id: event.id,
            },
          },
        ),
      isHidden: !hasPermission(canManageEvents),
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      "data-testid": "context-menu-delete-btn",
      icon: <Trash01Icon />,
      type: "button",
      isHidden: !event.canDelete,
      callbackFn: () =>
        openDeleteModal({
          id: event.id,
        }),
    },
  ];

  const maxGuests = computeMaxGuests(event);

  const handleToggleJoin = () => {
    if (event.isSignedUp) {
      signOffMutation.mutate(event.id);
    } else if (maxGuests <= 1) {
      signUpMutation.mutate({ eventId: event.id, guestCount: 0 });
    } else {
      signUpWithGuestsModalHandler.setTrue();
    }
  };

  const handleSignUpWithGuests = (guestCount: number) => {
    signUpMutation.mutate(
      { eventId: event.id, guestCount },
      { onSuccess: () => signUpWithGuestsModalHandler.setFalse() },
    );
  };

  const handleJoinExternalEvent = () => {
    window.open(event.externalPartyUrl, "_blank");

    confirmExternalSignUpModalHandler.setTrue();
  };

  const isJoinButtonVisible = canParticipateEvent(event);

  const amnountParticipants = event.deletedAt ? 0 : event.participantCount;
  const participantsExist = isDefined(event.maxParticipants) && isDefined(event.participantCount);
  const isFull = participantsExist ? event.participantCount! >= event.maxParticipants! : false;
  const restSpotsAmount = participantsExist && !isFull ? event.maxParticipants! - event.participantCount! : null;
  const numberOfFreeSpots = (() => {
    if (event.maxParticipants != null && amnountParticipants != null) {
      const result = event.maxParticipants - amnountParticipants;

      return result <= 0 ? Infinity : result;
    }

    return Infinity;
  })();
  const isLastSpots = numberOfFreeSpots <= 3;
  const isStatusAccented = !event.isSignedUp && isLastSpots;
  const labelParticipants =
    event.maxParticipants === null
      ? String(amnountParticipants || 0)
      : `${amnountParticipants || 0}/${event.maxParticipants}`;
  const statusLabel: string | null = (() => {
    if (event.deletedAt) return t("model.event.status.deleted");
    if (event.isSignedUp) return t("model.event.status.is-signed-up");
    if (isFull) return t("model.event.status.no-spots");
    if (!event.canParticipate) return null;
    if (isDefined(restSpotsAmount))
      return t("model.event.status.spots", { count: event.maxParticipants! - event.participantCount! });

    return null;
  })();

  const infoItems = [
    {
      icon: <CalendarIcon />,
      label: t("model.event.details.time"),
      value: <DateRange start={event.startsAt} end={event.endsAt} />,
    },
    ...(event.location != null
      ? [
          {
            icon: <MarkerPin01Icon />,
            label: t("model.event.details.location"),
            value: event.location,
          },
        ]
      : []),
    {
      icon: <User01Icon />,
      label: t("model.event.details.organizer"),
      value: <UserFullNameLink data-testid="event-organizer-link" user={event.organizer} />,
    },
    ...(statusLabel != null
      ? [
          {
            icon: <Users01Icon />,
            label: t("model.event.details.participants"),
            value: (
              <Inline align="center" wrap={false}>
                {labelParticipants}
                <span
                  className={twJoin(
                    "ml-space-xs flex grow-0 items-center gap-space-xs",
                    isStatusAccented ? "text-text-danger" : undefined,
                  )}
                >
                  <span className="block size-status-dot shrink-0 rounded-full bg-current" />
                  {statusLabel}
                </span>
              </Inline>
            ),
          },
        ]
      : []),
    ...(!event.deletedAt && event.updatedAt
      ? [
          {
            icon: <Edit05Icon />,
            label: t("model.event.details.last-edited"),
            value: <FormattedDate date={event.updatedAt} format="datetime" />,
          },
        ]
      : []),
  ].filter(isDefined);

  return (
    <>
      <EntityDetailsCard
        title={event.name}
        description={event.description}
        images={resolvedImage ? [resolvedImage] : []}
        actions={menuActions}
        infoItems={infoItems}
        renderBadges={() => <EventBadges event={event} />}
        renderCtaButton={
          isJoinButtonVisible
            ? () => {
                if (event.type === "external") {
                  return (
                    // Same look & feel button but no toggling state
                    <ToggleButton
                      isChecked={false}
                      config={{
                        checked: {
                          label: t("model.event.details.btn.sign-off"),
                          icon: <CheckIcon />,
                        },
                        unchecked: {
                          label: t("model.event.details.btn.sign-up"),
                          icon: <LinkExternal01Icon />,
                        },
                      }}
                      fullWidth="untilSm"
                      disabled={isPast(parseISO(event.endsAt)) || !!event.deletedAt}
                      data-testid="event-external-sign-up-btn"
                      onClick={handleJoinExternalEvent}
                    />
                  );
                }

                return (
                  <ToggleButton
                    isLoading={signOffMutation.isPending || signUpMutation.isPending}
                    isChecked={event.isSignedUp}
                    config={{
                      checked: {
                        label: t("model.event.details.btn.sign-off"),
                        icon: <CheckIcon />,
                      },
                      unchecked: {
                        label: t("model.event.details.btn.sign-up"),
                        icon: <PlusIcon />,
                      },
                    }}
                    fullWidth="untilSm"
                    disabled={
                      !(event.isSignedUp || event.canParticipate) || isPast(parseISO(event.endsAt)) || !!event.deletedAt
                    }
                    data-testid="event-participation-toggle"
                    onClick={handleToggleJoin}
                  />
                );
              }
            : undefined
        }
        renderBody={
          event.audience.length > 0 && sessionUser.isAdmin && hasPermission(canManageEvents)
            ? () => (
                <div className="w-fit">
                  <AudiencePreview audience={event.audience} readOnly />
                </div>
              )
            : undefined
        }
        renderFooter={
          isContentAutoTranslated(event.originalLanguageId, sessionUser) ? () => <AutoTranslatedLabel /> : undefined
        }
        entityId={event.id}
        entityType="event"
        allowShare={getIsEventSharable(event)}
      />
      <EventDetailsConfirmExternalEventSignUpModal
        isOpened={isConfirmExternalSignUpModalOpened}
        onOpenChange={confirmExternalSignUpModalHandler.set}
      />
      <EventSignUpWithGuestsModal
        eventName={event.name}
        isOpened={isSignUpWithGuestsModalOpen}
        onOpenChange={signUpWithGuestsModalHandler.set}
        maxGuests={maxGuests}
        isLoading={signUpMutation.isPending}
        onConfirm={handleSignUpWithGuests}
      />
      <DeleteModal
        title={t("model.event.action.delete.confirmation")}
        description={t("model.event.action.delete.description")}
        onDelete={() => deleteEventMutation.mutateAsync(event.id)}
        {...componentProps}
      />
    </>
  );
}
